import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full rounded-lg border border-gold/40 bg-white px-3 py-2.5 text-sm outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/25";

export default function Settings() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) return toast.error("New passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — use it next time you log in");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md" data-testid="settings-panel">
      <div className="rounded-3xl border border-gold/40 bg-white/85 p-5 shadow-sm backdrop-blur sm:p-7" data-testid="change-password-card">
        <h3 className="flex items-center gap-2 font-serif text-xl font-semibold text-burgundy">
          <KeyRound className="h-5 w-5 text-gold" /> Change Password
        </h3>
        <p className="mt-1 text-xs text-plum/55">
          Updates your admin login password straight away. Forgotten it completely? Log out and use
          “Forgot your password?” on the login page — a reset link goes to your Gmail.
        </p>
        <form onSubmit={submit} className="mt-5 space-y-3" data-testid="change-password-form">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-wine/60">Current password</label>
            <input
              type="password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="change-current-password"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-wine/60">New password</label>
            <input
              type="password"
              required
              minLength={8}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="change-new-password"
              className={inputCls}
              placeholder="At least 8 characters"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-wine/60">Confirm new password</label>
            <input
              type="password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="change-confirm-password"
              className={inputCls}
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="change-password-submit"
            className="flex items-center gap-2 rounded-full border border-gold bg-gold px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-white transition-all hover:bg-golddeep disabled:opacity-50"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Update password
          </button>
        </form>
      </div>
    </div>
  );
}
